import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { fetchFundingDetails, updateFundingDetails } from '../../../../store/startup/funding/fundingSlice';
import {
    Box,
    Typography,
    TextField,
    Alert,
    Switch,
    FormControlLabel,
    IconButton,
    Button,
    Paper,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Grid,
    useTheme,
    alpha,
    InputAdornment
} from '@mui/material';
import { HelpOutline, ExpandMore } from '@mui/icons-material';
import FundUsage from './FundUsage';
import FundGeneralJustification from './FundGeneralJustification';

const Funding = () => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const { startupId } = useParams();
    const { details, loading } = useSelector(state => state.funding);

    const [formData, setFormData] = useState({
        fundingNeeded: '',
        fundingDurationMonths: '',
        equityOffered: '',
        ekFonlama: false
    });

    const [errors, setErrors] = useState({
        fundingNeeded: false,
        fundingDurationMonths: false,
        equityOffered: false
    });

    useEffect(() => {
        dispatch(fetchFundingDetails(startupId));
    }, [dispatch, startupId]);

    useEffect(() => {
        if (details) {
            setFormData(prev => ({
                ...prev,
                fundingNeeded: details.fundingNeeded || '',
                fundingDurationMonths: details.fundingDurationMonths || '',
                equityOffered: details.equityOffered || '',
                ekFonlama: details.ekFonlama || false
            }));
        }
    }, [details]);

    const calculateValuation = () => {
        if (formData.fundingNeeded && formData.equityOffered) {
            const funding = parseFloat(formData.fundingNeeded);
            const equity = parseFloat(formData.equityOffered);
            if (!isNaN(funding) && !isNaN(equity) && equity > 0) {
                return (funding * 100 / equity).toFixed(2);
            }
        }
        return '0';
    };

    const calculateShareNominalValue = () => {
        const funding = parseFloat(formData.fundingNeeded) || 0;
        const equity = parseFloat(formData.equityOffered) || 0;
        const valuation = parseFloat(calculateValuation()) || 0;

        if (funding > 0 && equity > 0) {
            return ((funding + valuation) * equity / funding).toFixed(2);
        }
        return '0';
    };

    // Total Shares hesaplama fonksiyonu
    const calculateTotalShares = () => {
        const funding = parseFloat(formData.fundingNeeded);
        if (!isNaN(funding) && funding > 0) {
            return funding.toFixed(2);
        }
        return '0';
    };

    // Post Funding Capital hesaplama fonksiyonu
    const calculatePostFundingCapital = () => {
        const totalShares = parseFloat(calculateTotalShares());
        if (!isNaN(totalShares) && totalShares > 0) {
            return totalShares.toFixed(2);
        }
        return '0';
    };

    const handleChange = (event) => {
        const { name, value, checked } = event.target;
        const newValue = event.target.type === 'checkbox' ? checked : value;

        setFormData(prev => ({
            ...prev,
            [name]: newValue
        }));
    };

    const handleSave = async () => {
        const newErrors = {
            fundingNeeded: !formData.fundingNeeded.toString().trim(),
            fundingDurationMonths: !formData.fundingDurationMonths.toString().trim(),
            equityOffered: !formData.equityOffered.toString().trim()
        };

        if (Object.values(newErrors).some(error => error)) {
            setErrors(newErrors);
            return;
        }

        try {
            await dispatch(updateFundingDetails({
                startupId,
                ...formData
            })).unwrap();
        } catch (error) {
            console.error('Failed to update funding details:', error);
        }
    };

    const renderTextField = (label, name, type = "text") => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <TextField
                required
                fullWidth
                label={label}
                name={name}
                type={type}
                value={formData[name]}
                onChange={handleChange}
                disabled={loading}
                error={errors[name]}
                helperText={errors[name] ? `${label} boş bırakılamaz` : ""}
                sx={{
                    '& .MuiOutlinedInput-root': {
                        '&:hover fieldset': {
                            borderColor: theme.palette.primary.main,
                        },
                    },
                }}
            />
            <IconButton sx={{
                color: theme.palette.primary.main,
                '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.08),
                }
            }}>
                <HelpOutline />
            </IconButton>
        </Box>
    );

    return (
        <Box>
            <Alert severity="info" sx={{ mb: 4 }}>
                Platformumuzda çıkarılan her bir pay için 1 TL sabit değer olarak alınır. Bir Girişimci bir yıl içerisinde iki kampanya yapabilir ve bu kampanyalarda talep edilen fon tutarı 2025 yılı için 112.500.000 TL'den fazla olamaz.
            </Alert>

            <Grid item container spacing={2}>
                <Grid item xs={6}>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Girişimin Değeri"
                                value={calculateValuation()}
                                disabled
                                InputProps={{
                                    readOnly: true,
                                    startAdornment: <InputAdornment position="start">₺</InputAdornment>,
                                }}
                                helperText="Bu değer, talep edilen fon ve önerilen hisse oranına göre otomatik hesaplanır"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            {renderTextField("Fon Kullanım Süresi (Ay)", "fundingDurationMonths", "number")}
                        </Grid>
                        <Grid item xs={12}>
                            {renderTextField("Girişimin İçin ne Kadar Fona İhtiyacın Var?", "fundingNeeded")}
                        </Grid>
                        <Grid item xs={12}>
                            {renderTextField("Bu Fon için % Kaç Hisse Vermek İstiyorsun?", "equityOffered")}
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item xs={6}>
                    <Paper sx={{ p: 4, mb: 0, bgcolor: alpha(theme.palette.primary.main, 0.03) }}>
                        <Typography variant="h6" gutterBottom color="primary">
                            Kampanya Sonrası Oluşan Değerler
                        </Typography>

                        <Grid container spacing={3}>
                            {[
                                { 
                                    label: "Toplam Satışa Sunulan Pay Adedi", 
                                    value: calculateTotalShares(),
                                    tooltip: "Bu değer, talep edilen fon tutarına eşittir"
                                },
                                { 
                                    label: "Fonlama Sonrası Sermaye", 
                                    value: `₺${calculatePostFundingCapital()}`,
                                    tooltip: "Bu değer, toplam satışa sunulan pay adedine eşittir"
                                },
                                { 
                                    label: "Payların Birim Satış Fiyatı", 
                                    value: `₺${details?.shareUnitPrice || 0}` 
                                },
                                { 
                                    label: "Payların Birim Nominal Değeri", 
                                    value: `₺${calculateShareNominalValue()}`,
                                    tooltip: "Bu değer, (Talep Edilen Fon + Girişim Değeri) × Hisse Oranı / Talep Edilen Fon formülü ile hesaplanır"
                                }
                            ].map((item, index) => (
                                <Grid item xs={12} key={index}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography variant="body1" color="textSecondary">
                                            {item.label}
                                        </Typography>
                                        {item.tooltip && (
                                            <IconButton 
                                                size="small" 
                                                title={item.tooltip}
                                            >
                                                <HelpOutline fontSize="small" />
                                            </IconButton>
                                        )}
                                        <Typography variant="h6" color="primary">
                                            {item.value}
                                        </Typography>
                                    </Box>
                                </Grid>
                            ))}
                        </Grid>
                    </Paper>
                </Grid>
            </Grid>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={formData.ekFonlama}
                                onChange={handleChange}
                                name="ekFonlama"
                            />
                        }
                        label="%20'ye Kadar Ek Fonlama Yapmak İstiyor musunuz?"
                    />
                    <IconButton>
                        <HelpOutline />
                    </IconButton>
                </Box>
                <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={loading}
                    sx={{
                        bgcolor: theme.palette.primary.main,
                        '&:hover': {
                            bgcolor: theme.palette.primary.dark,
                        },
                    }}
                >
                    {loading ? 'Kaydediliyor...' : 'Kaydet'}
                </Button>
            </Box>

            <FundUsage />
            <FundGeneralJustification />
        </Box>
    );
};

export default Funding;
